import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FaFilePdf, FaWhatsapp } from 'react-icons/fa';
import { enlaceDeBases, whatsappShareUrl } from '../services/convocatoriaShare';
import { supabase } from '../services/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Modal, Spinner } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  RefreshCw,
  CheckCheck,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Calendar as CalendarIcon,
  CalendarCheck,
  ExternalLink,
  Inbox,
  Timer,
  ClipboardList,
  CalendarDays,
  CornerDownRight,
  Clock,
  Copy
} from 'lucide-react';
import './newmenores.scss';
import './menores-vigentes.scss';

// Tabla con los contratos menores que siguen abiertos a cotización. Tiene la
// misma forma que `contratos_menores`, pero sin el recorte a lo publicado hoy.
const TABLA_VIGENTES = 'menores_vigentes';

// Supabase corta cada consulta en 1000 filas: las vigentes se traen por lotes.
const BATCH_SIZE = 500;

// Cache global para almacenar los datos
let globalDataCache = {
  data: null,
  timestamp: null,
  isLoading: false
};

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos en milisegundos

// Función utilitaria para convertir fecha al formato ISO
const convertToISO = (dateString) => {
  if (!dateString || dateString.trim() === '' || dateString === '-') {
    return null;
  }
  try {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateString)) {
      return dateString;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}\s\d{1,2}:\d{1,2}:\d{1,2}$/.test(dateString.trim())) {
      const [datePart, timePart] = dateString.trim().split(' ');
      const [day, month, year] = datePart.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString.trim())) {
      const [day, month, year] = dateString.trim().split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`;
    }
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString.trim())) {
      const [day, month, year] = dateString.trim().split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`;
    }
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
    return null;
  } catch (error) {
    console.error('Error convirtiendo fecha:', error);
    return null;
  }
};

// ==========================================
// Helpers de fecha / urgencia / tipo
// ==========================================
const two = (n) => String(n).padStart(2, '0');

const parseDateTime = (raw) => {
  const iso = convertToISO(raw);
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const fmtFechaLarga = (d) =>
  d ? d.toLocaleDateString('es-PE', { day: 'numeric', month: 'long' }) : '';
const fmtFecha = (d) => (d ? `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}` : '—');
const fmtCorto = (d) => (d ? `${two(d.getDate())}/${two(d.getMonth() + 1)}` : '—');
const fmtHora = (d) => (d ? `${two(d.getHours())}:${two(d.getMinutes())}` : '');
const mismoDia = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Formatea un tiempo restante en ms como "2 h 09 min", "45 min" o "3 días"
const fmtRestante = (ms) => {
  if (ms === null || ms === undefined || ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const totalH = Math.floor(totalMin / 60);
  if (totalH < 24) {
    const min = totalMin % 60;
    return min > 0 ? `${totalH} h ${two(min)} min` : `${totalH} h`;
  }
  const dias = Math.floor(totalH / 24);
  return dias === 1 ? '1 día' : `${dias} días`;
};

// Calcula la urgencia del cierre de cotización a partir de la fecha fin
const getUrgencia = (process) => {
  const fin = parseDateTime(process._fin_cotizacion_raw);
  if (!fin) return { label: null, level: 'none', dayDiff: null, closed: false };
  const now = new Date();
  const diffMs = fin - now;
  const startFin = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
  const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayDiff = Math.round((startFin - startNow) / 86400000);

  if (diffMs <= 0) return { label: 'Culminada', level: 'closed', dayDiff, closed: true };
  if (dayDiff <= 0) {
    const h = Math.max(1, Math.ceil(diffMs / 3600000));
    return { label: `Cierra en ${h} h`, level: 'urgent', dayDiff: 0, closed: false };
  }
  if (dayDiff === 1) return { label: 'Cierra mañana', level: 'soon', dayDiff: 1, closed: false };
  return { label: `Cierra en ${dayDiff} días`, level: 'normal', dayDiff, closed: false };
};

const getTipoInfo = (tipo) => {
  const t = (tipo || '').toUpperCase();
  if (t.startsWith('BIEN')) return { cls: 'bien', label: 'Bien' };
  if (t.startsWith('SERV')) return { cls: 'servicio', label: 'Servicio' };
  if (t.startsWith('OBRA')) return { cls: 'obra', label: 'Obra' };
  if (t.startsWith('CONSULT')) return { cls: 'consultoria', label: 'Consultoría' };
  return { cls: 'otro', label: tipo || 'N/A' };
};

const CIERRE_LABELS = {
  todos: 'Cualquier cierre',
  hoy: 'Cierran hoy',
  manana: 'Cierran mañana',
  '3dias': 'Próximos 3 días',
  '7dias': 'Próximos 7 días',
  '15dias': 'Próximos 15 días',
  '30dias': 'Próximos 30 días'
};
const ESTADO_LABELS = {
  todos: 'Mostrar todos',
  pendientes: 'Pendientes',
  revisados: 'Revisadas'
};
const ORDEN_LABELS = {
  cierre_asc: 'Cierre más próximo',
  cierre_desc: 'Cierre más lejano',
  pub_desc: 'Publicación reciente',
  entidad: 'Entidad (A–Z)'
};

// ==========================================
// Checkbox de revisión (celda REV / modal)
// ==========================================
const RowCheckbox = ({ checked, onToggle, disabled }) => (
  <button
    type="button"
    className={`nm-check ${checked ? 'is-on' : ''}`}
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled) onToggle();
    }}
    disabled={disabled}
    aria-pressed={checked}
    title={checked ? 'Marcada como revisada' : 'Marcar como revisada'}
  >
    {checked && <Check size={13} strokeWidth={3} />}
  </button>
);

// ==========================================
// Botón "Agregar al calendario"
// ==========================================
// `enCalendario` es el Set de códigos ya guardados, que la página lee de una
// sola vez. Antes cada botón consultaba Supabase por su cuenta: con 20 filas
// en pantalla eran 20 peticiones para pintar un icono.
const CalendarButton = ({ process, user, variant = 'link', enCalendario, onAdded }) => {
  const [adding, setAdding] = useState(false);

  const userIdentifier = user?.email || user?.id || user?.user_metadata?.email;
  const hasValidUser = Boolean(userIdentifier);
  const codContratacion = process['COD CONTRATACIÓN'];
  const added = Boolean(enCalendario && codContratacion && enCalendario.has(codContratacion));

  const addToCalendar = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasValidUser) {
      toast.error('No se pudo identificar tu cuenta. Verifica tu sesión.', { containerId: 'mv-calendar' });
      return;
    }
    if (added) {
      toast.info('Este proceso ya está agregado al calendario.', { containerId: 'mv-calendar' });
      return;
    }
    setAdding(true);
    try {
      const eventos = [];
      if (process._inicio_cotizacion_raw) {
        const fechaInicioISO = convertToISO(process._inicio_cotizacion_raw);
        if (fechaInicioISO) {
          eventos.push({
            user_id: userIdentifier,
            nomenclatura: process['COD CONTRATACIÓN'],
            entidad: process.ENTIDAD,
            descripcion_proceso: process['DESCRIPCIÓN'],
            tipo_evento: 'inicio_registro',
            fecha_evento: fechaInicioISO,
            fuente: 'CONTRATOS_MENORES',
            departamento: process.DPTO,
            objeto: process.TIPO,
            created_at: new Date().toISOString()
          });
        }
      }
      if (process._fin_cotizacion_raw) {
        const fechaFinISO = convertToISO(process._fin_cotizacion_raw);
        if (fechaFinISO) {
          eventos.push({
            user_id: userIdentifier,
            nomenclatura: process['COD CONTRATACIÓN'],
            entidad: process.ENTIDAD,
            descripcion_proceso: process['DESCRIPCIÓN'],
            tipo_evento: 'fin_registro',
            fecha_evento: fechaFinISO,
            fuente: 'CONTRATOS_MENORES',
            departamento: process.DPTO,
            objeto: process.TIPO,
            created_at: new Date().toISOString()
          });
        }
      }
      if (eventos.length > 0) {
        const { error } = await supabase.from('calendar_events').insert(eventos);
        if (error) throw error;
        if (onAdded) onAdded(eventos);
        toast.success(
          eventos.length === 1 ? 'Se agregó 1 evento al calendario.' : `Se agregaron ${eventos.length} eventos al calendario.`,
          { containerId: 'mv-calendar' }
        );
      } else {
        toast.warning('No hay fechas disponibles para agregar al calendario.', { containerId: 'mv-calendar' });
      }
    } catch (error) {
      console.error('Error agregando al calendario:', error);
      toast.error('No se pudo agregar al calendario. Intenta nuevamente.', { containerId: 'mv-calendar' });
    } finally {
      setAdding(false);
    }
  };

  if (variant === 'seg') {
    return (
      <button
        type="button"
        className={`nm-seg-btn ${added ? 'is-on' : ''}`}
        onClick={addToCalendar}
        disabled={!hasValidUser || adding || added}
      >
        {added ? <CalendarCheck size={16} /> : <CalendarIcon size={16} />}
        <span>{adding ? 'Agregando…' : added ? 'Agregado al calendario' : 'Añadir al calendario'}</span>
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        type="button"
        className={`nm-icon-btn ${added ? 'is-added' : ''}`}
        onClick={addToCalendar}
        disabled={!hasValidUser || adding || added}
        title={added ? 'Agregado al calendario' : 'Añadir al calendario'}
      >
        {added ? <CalendarCheck size={17} /> : <CalendarIcon size={17} />}
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <button
        type="button"
        className={`nm-cal-btn ${added ? 'is-added' : ''}`}
        onClick={addToCalendar}
        disabled={!hasValidUser || adding || added}
      >
        {added ? <CalendarCheck size={16} /> : <CalendarIcon size={16} />}
        <span>{adding ? 'Agregando…' : added ? 'Agregado' : 'Agregar al calendario'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`nm-cal ${added ? 'is-added' : ''}`}
      onClick={addToCalendar}
      disabled={!hasValidUser || adding || added}
    >
      {added ? <CalendarCheck size={15} /> : <CalendarIcon size={15} />}
      <span>{adding ? 'Agregando…' : added ? 'Agregado' : 'Calendario'}</span>
    </button>
  );
};

const CopyNomenclatura = ({ code }) => {
  const [status, setStatus] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(String(code));
      setStatus('Copiado');
    } catch {
      setStatus('No se pudo copiar');
    }
    timer.current = setTimeout(() => setStatus(''), 2000);
  };
  return (
    <span className="nm-copy-code">
      <button type="button" className="conv-code nm-copy-button" onClick={copy}
        disabled={!code || code === '-'} title="Copiar nomenclatura"
        aria-label={`Copiar nomenclatura ${code || ''}`}>
        {code || '-'}
      </button>
      <span className="nm-copy-status" role="status">{status}</span>
    </span>
  );
};

const MenoresVigentes = ({ user }) => {
  const navigate = useNavigate();
  // Estados principales
  const [filasCargadas, setFilasCargadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [progreso, setProgreso] = useState(0);

  // Sistema de revisión
  const [processosRevisados, setProcessosRevisados] = useState(new Set());
  const [loadingReviewed, setLoadingReviewed] = useState(false);
  const [filtroEstadoRevision, setFiltroEstadoRevision] = useState('todos');

  // Filtros
  const [departamentosSeleccionados, setDepartamentosSeleccionados] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCierre, setFiltroCierre] = useState('todos');
  const [busquedaGlobal, setBusquedaGlobal] = useState('');
  const [todosDepartamentos, setTodosDepartamentos] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState('cierre_asc');

  // Dropdowns / paneles
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchDepartamento, setSearchDepartamento] = useState('');

  // Modal
  const [selectedContract, setSelectedContract] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Responsividad
  const [isMobile, setIsMobile] = useState(false);

  const dropdownRef = useRef(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 820);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Cargar procesos revisados
  const loadUserReviewedProcesses = async (user) => {
    if (!user) return;
    const userIdentifier = user?.email || user?.id || user?.user_metadata?.email;
    if (!userIdentifier) return;
    try {
      setLoadingReviewed(true);
      const { data, error } = await supabase
        .from('user_reviewed_processes')
        .select('process_id')
        .eq('user_id', userIdentifier);
      if (error) throw error;
      setProcessosRevisados(new Set(data.map((item) => item.process_id)));
    } catch (error) {
      console.error('Error cargando procesos revisados:', error);
    } finally {
      setLoadingReviewed(false);
    }
  };

  const toggleProcessReviewed = async (process, user) => {
    const userIdentifier = user?.email || user?.id || user?.uid;
    if (!userIdentifier) {
      alert('Usuario no identificado. Por favor, verifica tu sesión e intenta nuevamente.');
      return;
    }
    try {
      const processId = `${process['COD CONTRATACIÓN']}-${process['DESCRIPCIÓN']}`;
      const isCurrentlyReviewed = processosRevisados.has(processId);

      if (isCurrentlyReviewed) {
        const { error } = await supabase
          .from('user_reviewed_processes')
          .delete()
          .eq('user_id', userIdentifier)
          .eq('process_id', processId);
        if (error) throw error;
        setProcessosRevisados((prev) => {
          const newSet = new Set(prev);
          newSet.delete(processId);
          return newSet;
        });
      } else {
        const { error } = await supabase.from('user_reviewed_processes').insert({
          user_id: userIdentifier,
          process_id: processId,
          nomenclatura: process['COD CONTRATACIÓN']
        });
        if (error) throw error;
        setProcessosRevisados((prev) => new Set([...prev, processId]));
      }
    } catch (error) {
      console.error('Error:', error);
      alert(`Error al actualizar estado: ${error.message}`);
    }
  };

  const isProcessReviewed = (process) => {
    const processId = `${process['COD CONTRATACIÓN']}-${process['DESCRIPCIÓN']}`;
    return processosRevisados.has(processId);
  };

  const markAllAsReviewed = async () => {
    const userIdentifier = user?.email || user?.id || user?.user_metadata?.email;
    if (!userIdentifier) {
      alert('Usuario no identificado. Por favor, verifica tu sesión.');
      return;
    }
    const procesosNoRevisados = dataFiltrada.filter((proceso) => !isProcessReviewed(proceso));
    if (procesosNoRevisados.length === 0) {
      alert('Todos los procesos ya están marcados como revisados.');
      return;
    }
    const confirmacion = window.confirm(
      `¿Estás seguro de marcar ${procesosNoRevisados.length} procesos como revisados?`
    );
    if (!confirmacion) return;
    // Aquí la lista filtrada puede tener miles de filas: se inserta por lotes
    // en lugar de lanzar una petición por convocatoria.
    const filas = procesosNoRevisados.map((proceso) => ({
      user_id: userIdentifier,
      process_id: `${proceso['COD CONTRATACIÓN']}-${proceso['DESCRIPCIÓN']}`,
      nomenclatura: proceso['COD CONTRATACIÓN']
    }));
    try {
      for (let i = 0; i < filas.length; i += BATCH_SIZE) {
        const { error } = await supabase
          .from('user_reviewed_processes')
          .insert(filas.slice(i, i + BATCH_SIZE));
        if (error) throw error;
      }
      setProcessosRevisados((prev) => new Set([...prev, ...filas.map((f) => f.process_id)]));
      alert(`Se marcaron ${filas.length} procesos como revisados.`);
    } catch (error) {
      console.error('Error marcando todos como revisados:', error);
      alert('Hubo un error al marcar los procesos. Intenta nuevamente.');
    }
  };

  // Cache
  const isCacheValid = () => {
    if (!globalDataCache.data || !globalDataCache.timestamp) return false;
    return Date.now() - globalDataCache.timestamp < CACHE_DURATION;
  };

  const loadDataIfNeeded = async () => {
    if (isCacheValid() && !globalDataCache.isLoading) {
      setFilasCargadas(globalDataCache.data);
      setLastUpdated(new Date(globalDataCache.timestamp));
      return;
    }
    if (globalDataCache.isLoading) {
      if (globalDataCache.data) {
        setFilasCargadas(globalDataCache.data);
        setLastUpdated(new Date(globalDataCache.timestamp));
      }
      return;
    }
    await loadData();
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      loadDataIfNeeded();
      if (user?.email) loadUserReviewedProcesses(user);
    }
  }, [user]);

  useEffect(() => {
    if (user && processosRevisados.size === 0 && !loadingReviewed) {
      loadUserReviewedProcesses(user);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // El orden por `id` es lo único estable para paginar: las fechas son texto
  // y llegan en dos formatos distintos.
  const cargarPorLotes = async (total) => {
    const filas = [];
    const lotes = Math.max(1, Math.ceil(total / BATCH_SIZE));
    for (let i = 0; i < lotes; i++) {
      const desde = i * BATCH_SIZE;
      const hasta = Math.min(desde + BATCH_SIZE - 1, total - 1);
      const { data: lote, error } = await supabase
        .from(TABLA_VIGENTES)
        .select('*')
        .order('id', { ascending: true })
        .range(desde, hasta);
      if (error) throw error;
      if (lote && lote.length) filas.push(...lote);
      setProgreso(Math.round(((i + 1) / lotes) * 100));
    }
    return filas;
  };

  const loadData = async (forceRefresh = false) => {
    if (globalDataCache.isLoading && !forceRefresh) return;
    setLoading(true);
    setError(null);
    setProgreso(0);
    globalDataCache.isLoading = true;
    try {
      const { count, error: errorConteo } = await supabase
        .from(TABLA_VIGENTES)
        .select('*', { count: 'exact', head: true });
      if (errorConteo) throw errorConteo;

      const contratos = count ? await cargarPorLotes(count) : [];

      const formateado = contratos.map((row) => ({
        'COD CONTRATACIÓN': row.cod_contratacion,
        TIPO: row.tipo,
        DPTO: row.departamento,
        PROVINCIA: row.provincia,
        DISTRITO: row.distrito,
        ENTIDAD: row.entidad,
        DESCRIPCIÓN: row.descripcion,
        'INICIO COTIZACIÓN': row.inicio_cotizacion ? formatDate(row.inicio_cotizacion) : '-',
        'FIN COTIZACIÓN': row.fin_cotizacion ? formatDate(row.fin_cotizacion) : '-',
        'FECHA PUBLICACIÓN': row.fecha_publicacion ? formatDate(row.fecha_publicacion) : '-',
        URL_TDR: row.url_publico_tdr,
        _inicio_cotizacion_raw: row.inicio_cotizacion,
        _fin_cotizacion_raw: row.fin_cotizacion,
        _fecha_publicacion_raw: row.fecha_publicacion
      }));

      const ordenado = formateado.sort((a, b) => (a.DPTO || '').localeCompare(b.DPTO || ''));

      globalDataCache.data = ordenado;
      globalDataCache.timestamp = Date.now();
      setFilasCargadas(ordenado);
      setLastUpdated(new Date(globalDataCache.timestamp));
    } catch (error) {
      console.error('Error al cargar las convocatorias vigentes:', error);
      setError(
        error?.code === '42P01'
          ? `La tabla «${TABLA_VIGENTES}» no existe en Supabase.`
          : 'Error al cargar los datos. Por favor, intente nuevamente.'
      );
    } finally {
      setLoading(false);
      setProgreso(100);
      globalDataCache.isLoading = false;
    }
  };

  const refreshData = () => {
    globalDataCache.data = null;
    globalDataCache.timestamp = null;
    loadData(true);
  };

  // Una convocatoria culminada (su fin de cotización ya pasó) dejó de estar
  // vigente: sale del listado, de los filtros y de las métricas. Las filas sin
  // fecha de fin se quedan, porque no hay con qué descartarlas.
  const data = useMemo(
    () => filasCargadas.filter((row) => !getUrgencia(row).closed),
    [filasCargadas]
  );

  // Valores únicos
  const departamentosUnicos = useMemo(
    () => Array.from(new Set(data.map((item) => item.DPTO))).filter(Boolean).sort(),
    [data]
  );
  const tiposUnicos = useMemo(
    () => Array.from(new Set(data.map((item) => item.TIPO))).filter(Boolean).sort(),
    [data]
  );
  const departamentosFiltrados = useMemo(() => {
    if (!searchDepartamento.trim()) return departamentosUnicos;
    return departamentosUnicos.filter((dept) =>
      dept.toLowerCase().includes(searchDepartamento.toLowerCase())
    );
  }, [departamentosUnicos, searchDepartamento]);

  // Datos filtrados + ordenados
  const dataFiltrada = useMemo(() => {
    const q = busquedaGlobal.trim().toLowerCase();

    let filtered = data.filter((row) => {
      const coincideDpto =
        todosDepartamentos ||
        departamentosSeleccionados.length === 0 ||
        departamentosSeleccionados.includes(row.DPTO);

      const coincideBusqueda =
        q === '' ||
        (row['DESCRIPCIÓN'] || '').toLowerCase().includes(q) ||
        (row.ENTIDAD || '').toLowerCase().includes(q) ||
        (row['COD CONTRATACIÓN'] || '').toLowerCase().includes(q);

      const coincideTipo =
        filtroTipo === '' || (row.TIPO || '').toLowerCase() === filtroTipo.toLowerCase();

      const urg = getUrgencia(row);
      const dentroDe = (dias) =>
        urg.dayDiff !== null && urg.dayDiff >= 0 && urg.dayDiff <= dias && !urg.closed;
      const coincideCierre =
        filtroCierre === 'todos' ||
        (filtroCierre === 'hoy' && urg.dayDiff === 0 && !urg.closed) ||
        (filtroCierre === 'manana' && urg.dayDiff === 1) ||
        (filtroCierre === '3dias' && dentroDe(3)) ||
        (filtroCierre === '7dias' && dentroDe(7)) ||
        (filtroCierre === '15dias' && dentroDe(15)) ||
        (filtroCierre === '30dias' && dentroDe(30));

      const coincideEstadoRevision =
        filtroEstadoRevision === 'todos' ||
        (filtroEstadoRevision === 'pendientes' && !isProcessReviewed(row)) ||
        (filtroEstadoRevision === 'revisados' && isProcessReviewed(row));

      return (
        coincideDpto && coincideBusqueda && coincideTipo && coincideCierre && coincideEstadoRevision
      );
    });

    const ts = (raw) => {
      const d = parseDateTime(raw);
      return d ? d.getTime() : null;
    };

    const now = Date.now();
    filtered.sort((a, b) => {
      const finA = ts(a._fin_cotizacion_raw);
      const finB = ts(b._fin_cotizacion_raw);
      const closedA = finA !== null && finA <= now;
      const closedB = finB !== null && finB <= now;
      if (closedA !== closedB) return closedA ? 1 : -1;
      if (ordenarPor === 'entidad') {
        return (a.ENTIDAD || '').localeCompare(b.ENTIDAD || '');
      }
      if (ordenarPor === 'pub_desc') {
        return (ts(b._fecha_publicacion_raw) || 0) - (ts(a._fecha_publicacion_raw) || 0);
      }
      const fa = ts(a._fin_cotizacion_raw);
      const fb = ts(b._fin_cotizacion_raw);
      if (fa === null && fb === null) return 0;
      if (fa === null) return 1;
      if (fb === null) return -1;
      return ordenarPor === 'cierre_desc' ? fb - fa : fa - fb;
    });

    return filtered;
  }, [
    data,
    todosDepartamentos,
    departamentosSeleccionados,
    busquedaGlobal,
    filtroTipo,
    filtroCierre,
    filtroEstadoRevision,
    ordenarPor,
    processosRevisados
  ]);

  // Métricas del día (sobre el total, no el filtrado)
  // ── Convocatorias en el calendario del usuario ──────────────────────────
  // Una sola lectura por usuario: alimenta la casilla «En mi calendario» y el
  // estado de los botones de cada fila.
  // Solo la fuente CONTRATOS_MENORES: esta pantalla no debe contar el SEACE
  // ni el PAC, que tienen su propia casilla en sus páginas.
  const [calEventos, setCalEventos] = useState([]);
  const userIdCalendario = user?.email || user?.id || user?.user_metadata?.email;

  useEffect(() => {
    if (!userIdCalendario) { setCalEventos([]); return; }
    let vigente = true;
    (async () => {
      try {
        const { data: filas, error: err } = await supabase
          .from('calendar_events')
          .select('nomenclatura, fecha_evento, tipo_evento')
          .eq('user_id', userIdCalendario)
          .eq('fuente', 'CONTRATOS_MENORES');
        if (err) throw err;
        if (vigente) setCalEventos(filas || []);
      } catch (e) {
        console.error('Error leyendo el calendario del usuario:', e);
      }
    })();
    return () => { vigente = false; };
  }, [userIdCalendario]);

  const registrarEnCalendario = (eventos) => {
    setCalEventos((prev) => [
      ...prev,
      ...eventos.map(({ nomenclatura, fecha_evento, tipo_evento }) => ({ nomenclatura, fecha_evento, tipo_evento }))
    ]);
  };

  // Códigos ya guardados, para que cada botón sepa su estado sin consultar
  const codigosEnCalendario = useMemo(
    () => new Set(calEventos.map((ev) => ev.nomenclatura).filter(Boolean)),
    [calEventos]
  );

  // Convocatorias distintas con alguna fecha por delante, y cuántas cierran
  // dentro de la semana. Un acumulado histórico solo crecería.
  const calendarioKpi = useMemo(() => {
    const ahora = Date.now();
    const limite = ahora + 7 * 24 * 60 * 60 * 1000;
    const activas = new Set();
    const cierranSemana = new Set();
    calEventos.forEach((ev) => {
      if (!ev.nomenclatura) return;
      const t = new Date(ev.fecha_evento).getTime();
      if (isNaN(t) || t < ahora) return;
      activas.add(ev.nomenclatura);
      if (t <= limite && (ev.tipo_evento === 'fin_registro' || ev.tipo_evento === 'presentacion_propuestas')) {
        cierranSemana.add(ev.nomenclatura);
      }
    });
    return { activas: activas.size, cierranSemana: cierranSemana.size };
  }, [calEventos]);

  const metrics = useMemo(() => {
    const total = data.length;
    const revisadas = data.filter(isProcessReviewed).length;
    const pendientes = total - revisadas;
    const cierranHoy = data.filter((r) => {
      const u = getUrgencia(r);
      return u.dayDiff === 0 && !u.closed;
    }).length;
    const cierran7 = data.filter((r) => {
      const u = getUrgencia(r);
      return u.dayDiff !== null && u.dayDiff >= 0 && u.dayDiff <= 7 && !u.closed;
    }).length;
    const pct = total > 0 ? Math.round((revisadas / total) * 100) : 0;

    // Reparto por objeto de contratación, en el orden del mockup
    const conteoTipo = {};
    data.forEach((r) => {
      const cls = getTipoInfo(r.TIPO).cls;
      conteoTipo[cls] = (conteoTipo[cls] || 0) + 1;
    });
    const repartoTipo = [
      { cls: 'servicio', label: 'servicios' },
      { cls: 'bien', label: 'bienes' },
      { cls: 'obra', label: 'obras' },
      { cls: 'consultoria', label: 'consultorías' },
      { cls: 'otro', label: 'otros' }
    ]
      .filter((t) => conteoTipo[t.cls])
      .map((t) => ({ ...t, count: conteoTipo[t.cls] }));

    // Cotización que cierra antes (solo entre las que siguen abiertas)
    const ahora = Date.now();
    let proximaMs = null;
    data.forEach((r) => {
      const fin = parseDateTime(r._fin_cotizacion_raw);
      if (!fin) return;
      const restante = fin.getTime() - ahora;
      if (restante > 0 && (proximaMs === null || restante < proximaMs)) proximaMs = restante;
    });

    return { total, revisadas, pendientes, cierranHoy, cierran7, pct, repartoTipo, proximaMs };
  }, [data, processosRevisados]);

  // Paginación
  const dataPaginada = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return dataFiltrada.slice(start, start + itemsPerPage);
  }, [dataFiltrada, currentPage, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(dataFiltrada.length / itemsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [
    busquedaGlobal,
    filtroTipo,
    filtroCierre,
    filtroEstadoRevision,
    departamentosSeleccionados,
    todosDepartamentos,
    itemsPerPage
  ]);

  // Chips de filtros activos
  const activeChips = useMemo(() => {
    const chips = [];
    if (!todosDepartamentos && departamentosSeleccionados.length > 0) {
      chips.push({
        key: 'dpto',
        label: departamentosSeleccionados.join(', '),
        onRemove: () => setDepartamentosSeleccionados([])
      });
    }
    if (filtroTipo) {
      chips.push({ key: 'tipo', label: getTipoInfo(filtroTipo).label, onRemove: () => setFiltroTipo('') });
    }
    if (filtroCierre !== 'todos') {
      chips.push({ key: 'cierre', label: CIERRE_LABELS[filtroCierre], onRemove: () => setFiltroCierre('todos') });
    }
    if (filtroEstadoRevision !== 'todos') {
      chips.push({
        key: 'estado',
        label: ESTADO_LABELS[filtroEstadoRevision],
        onRemove: () => setFiltroEstadoRevision('todos')
      });
    }
    if (busquedaGlobal.trim()) {
      chips.push({ key: 'busqueda', label: `"${busquedaGlobal.trim()}"`, onRemove: () => setBusquedaGlobal('') });
    }
    return chips;
  }, [todosDepartamentos, departamentosSeleccionados, filtroTipo, filtroCierre, filtroEstadoRevision, busquedaGlobal]);

  const clearFilters = () => {
    setDepartamentosSeleccionados([]);
    setFiltroTipo('');
    setFiltroCierre('todos');
    setBusquedaGlobal('');
    setTodosDepartamentos(false);
    setFiltroEstadoRevision('todos');
    setSearchDepartamento('');
    setCurrentPage(1);
  };

  const openModal = (contract) => {
    setSelectedContract(contract);
    setModalOpen(true);
  };
  const closeModal = () => {
    setSelectedContract(null);
    setModalOpen(false);
  };

  const compartirWhatsApp = (proceso) => {
    const lineas = [
      '*Contratos menores · Convocatoria vigente*',
      '',
      '*Nomenclatura:* ' + (proceso['COD CONTRATACIÓN'] || 'No indicada'),
      '*Entidad:* ' + (proceso.ENTIDAD || 'No indicada'),
      '*Descripción:* ' + (proceso.DESCRIPCIÓN || 'No indicada'),
      '*Objeto:* ' + (proceso.TIPO || 'No indicado'),
      '*Ubicación:* ' + ([proceso.DPTO, proceso.PROVINCIA, proceso.DISTRITO].filter(Boolean).join(' · ') || 'No indicada'),
      '*Inicio de cotización:* ' + (proceso['INICIO COTIZACIÓN'] || 'No indicado'),
      '*Fin de cotización:* ' + (proceso['FIN COTIZACIÓN'] || 'No indicado')
    ];
    const enlace = enlaceDeBases(proceso.URL_TDR);
    if (enlace) lineas.push('', '*Requerimiento / PDF:* ' + enlace);
    lineas.push('', 'Compartido desde LicitaHUB');
    const ventana = window.open(whatsappShareUrl(lineas.join('\n')), '_blank');
    if (ventana) ventana.opener = null;
    else alert('Permite las ventanas emergentes para abrir WhatsApp.');
  };

  const openPDF = (url) => {
    if (url && url.trim() !== '') {
      window.open(convertGoogleDriveUrl(url), '_blank', 'noopener,noreferrer');
    } else {
      alert('No hay URL de TDR disponible para este contrato');
    }
  };

  const convertGoogleDriveUrl = (url) => {
    if (url.includes('drive.google.com')) {
      let fileId = '';
      if (url.includes('/file/d/')) {
        fileId = url.split('/file/d/')[1].split('/')[0];
      } else if (url.includes('id=')) {
        fileId = url.split('id=')[1].split('&')[0];
      }
      if (fileId) return `https://drive.google.com/file/d/${fileId}/view`;
    }
    return url;
  };

  function formatDate(dateString) {
    if (!dateString || dateString.trim() === '') return '-';
    try {
      if (/^\d{1,2}\/\d{1,2}\/\d{4}\s\d{1,2}:\d{2}$/.test(dateString.trim())) return dateString.trim();
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString.trim())) return dateString.trim();
      if (/^\d{1,2}-\d{1,2}-\d{4}\s\d{1,2}:\d{2}$/.test(dateString.trim()))
        return dateString.trim().replace(/-/g, '/');
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString.trim())) return dateString.trim().replace(/-/g, '/');
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateString.trim())) {
        const [year, month, day] = dateString.trim().split('-');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(dateString.trim())) {
        const [year, month, day] = dateString.trim().split('/');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      if (/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(dateString.trim())) {
        const parts = dateString.trim().split(/[T\s]/);
        const [year, month, day] = parts[0].split('-');
        const [hours, minutes] = parts[1].split(':');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year} ${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
      }
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = two(date.getDate());
        const month = two(date.getMonth() + 1);
        const year = date.getFullYear();
        const hours = two(date.getHours());
        const minutes = two(date.getMinutes());
        if (hours !== '00' || minutes !== '00') return `${day}/${month}/${year} ${hours}:${minutes}`;
        return `${day}/${month}/${year}`;
      }
      return dateString.trim();
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return dateString || '-';
    }
  }

  // Renderiza la celda de cotización: inicio → fin (compacto) + fecha de publicación
  const renderCotizacion = (proceso) => {
    const ini = parseDateTime(proceso._inicio_cotizacion_raw);
    const fin = parseDateTime(proceso._fin_cotizacion_raw);
    const pub = parseDateTime(proceso._fecha_publicacion_raw);

    let principal;
    if (ini && fin) {
      const finTxt = mismoDia(ini, fin) ? fmtHora(fin) : `${fmtCorto(fin)} ${fmtHora(fin)}`;
      principal = (
        <>
          {fmtCorto(ini)} {fmtHora(ini)} → <strong>{finTxt}</strong>
        </>
      );
    } else if (fin) {
      principal = (
        <>
          Cierra <strong>{fmtCorto(fin)} {fmtHora(fin)}</strong>
        </>
      );
    } else {
      principal = <span className="muted">Sin fecha</span>;
    }

    return (
      <>
        <div className="cot-main">{principal}</div>
        {pub && <div className="cot-sub">Publicada {fmtCorto(pub)} {fmtHora(pub)}</div>}
      </>
    );
  };

  const subtitleFecha = fmtFechaLarga(new Date());
  const subtitleHora = lastUpdated ? fmtHora(lastUpdated) : '—';

  const getDepartamentoLabel = () => {
    if (todosDepartamentos) return `Todos (${departamentosUnicos.length})`;
    if (departamentosSeleccionados.length === 0) return 'Todos los departamentos';
    if (departamentosSeleccionados.length === 1) return departamentosSeleccionados[0];
    return `${departamentosSeleccionados.length} seleccionados`;
  };

  const toggleDepartamento = (departamento) => {
    setTodosDepartamentos(false);
    setDepartamentosSeleccionados((prev) =>
      prev.includes(departamento) ? prev.filter((d) => d !== departamento) : [...prev, departamento]
    );
  };

  // Números de página a mostrar
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxToShow = isMobile ? 3 : 5;
    if (totalPages <= maxToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxToShow / 2));
      let end = Math.min(totalPages, start + maxToShow - 1);
      if (end - start + 1 < maxToShow) start = Math.max(1, end - maxToShow + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (!pages.includes(totalPages)) {
        pages.push('…');
        pages.push(totalPages);
      }
    }
    return pages;
  }, [totalPages, currentPage, isMobile]);

  const rangoDesde = dataFiltrada.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangoHasta = Math.min(currentPage * itemsPerPage, dataFiltrada.length);

  const renderPagination = (mobile = false) => (
    <div className={`nm-pagination ${mobile ? 'mobile' : ''}`}>
      <span className="pg-info">
        {mobile ? (
          <>
            <b>
              {rangoDesde}–{rangoHasta}
            </b>{' '}
            de {dataFiltrada.length}
          </>
        ) : (
          <>
            Mostrando{' '}
            <b>
              {rangoDesde}–{rangoHasta}
            </b>{' '}
            de {dataFiltrada.length} convocatorias
          </>
        )}
      </span>
      <div className="pg-right">
        {!mobile && (
          <div className="pg-perpage">
            <span>Por página</span>
            <div className="filter-select-native compact">
              <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                {[20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} />
            </div>
          </div>
        )}
        <div className="pg-pages">
          <button
            type="button"
            className="pg-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={17} />
          </button>
          {pageNumbers.map((p, i) =>
            p === '…' ? (
              <span key={`e${i}`} className="pg-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pg-btn ${p === currentPage ? 'is-active' : ''}`}
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            className="pg-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight size={17} />
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="nm mv">
      <ToastContainer
        containerId="mv-calendar"
        className="nm-calendar-notifications"
        position="top-right"
        autoClose={4000}
        hideProgressBar
        pauseOnFocusLoss
        pauseOnHover
        closeOnClick
        limit={3}
        style={{ zIndex: 13000 }}
      />
      {/* Cabecera */}
      <header className="nm-head">
        <div className="nm-head-text">
          <div className="nm-eyebrow">CONTRATOS MENORES</div>
          <h1 className="nm-title">Convocatorias Vigentes</h1>
          <p className="nm-subtitle">
            Abiertas a cotización al {subtitleFecha} · actualizado {subtitleHora}
          </p>
        </div>
        <div className="nm-head-actions">
          <button type="button" className="nm-btn ghost" onClick={refreshData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            <span>{loading ? 'Actualizando…' : 'Actualizar'}</span>
          </button>
          {user?.email && (
            <button
              type="button"
              className="nm-btn primary"
              onClick={markAllAsReviewed}
              disabled={loadingReviewed || dataFiltrada.length === 0}
            >
              <CheckCheck size={16} />
              <span>Marcar todas revisadas</span>
            </button>
          )}
        </div>
      </header>

      {error && <div className="nm-alert">⚠️ {error}</div>}

      {/* KPIs del día */}
      <section className="nm-kpis">
        <button
          type="button"
          className="nm-kpi k-cal"
          onClick={() => navigate('/Calendario')}
          title="Ver el calendario de procesos"
        >
          <div className="kpi-top">
            <span className="kpi-label">EN MI CALENDARIO</span>
            <span className="kpi-icon">
              <CalendarDays size={15} />
            </span>
          </div>
          <div className="kpi-value">{calendarioKpi.activas.toLocaleString('es-PE')}</div>
          <div className="kpi-foot spaced">
            {calendarioKpi.cierranSemana > 0
              ? <><b>{calendarioKpi.cierranSemana.toLocaleString('es-PE')}</b> {calendarioKpi.cierranSemana === 1 ? 'cierra' : 'cierran'} esta semana</>
              : calendarioKpi.activas > 0
                ? 'Ninguna cierra esta semana'
                : 'Aún no guardas convocatorias'}
            <span className="kpi-go">Ver calendario <CornerDownRight size={12} /></span>
          </div>
        </button>

        <div className="nm-kpi k-inbox">
          <div className="kpi-top">
            <span className="kpi-label">VIGENTES PARA COTIZAR</span>
            <span className="kpi-icon">
              <Inbox size={15} />
            </span>
          </div>
          <div className="kpi-value">{metrics.total}</div>
          <div className="kpi-foot spaced kpi-types">
            {metrics.repartoTipo.length ? metrics.repartoTipo.map((tipo) => (
              <span key={tipo.cls} className={`kpi-type kpi-type--${tipo.cls}`}>
                <strong>{tipo.count.toLocaleString('es-PE')}</strong>
                <span>{tipo.label}</span>
              </span>
            )) : 'Sin convocatorias vigentes'}
          </div>
        </div>

        <div className="nm-kpi k-hoy">
          <div className="kpi-top">
            <span className="kpi-label">CIERRAN HOY</span>
            <span className="kpi-icon warm">
              <Timer size={15} />
            </span>
          </div>
          <div className="kpi-value">{metrics.cierranHoy}</div>
          <div className="kpi-foot spaced">
            {metrics.cierran7 > 0 ? (
              <>
                <b>{metrics.cierran7.toLocaleString('es-PE')}</b>{' '}
                {metrics.cierran7 === 1 ? 'cierra' : 'cierran'} esta semana
              </>
            ) : metrics.proximaMs !== null ? (
              <>
                La más próxima cierra en <b>{fmtRestante(metrics.proximaMs)}</b>
              </>
            ) : (
              'Sin cotizaciones abiertas'
            )}
          </div>
        </div>

        <div className="nm-kpi k-pend">
          <div className="kpi-top">
            <span className="kpi-label">PENDIENTES</span>
            <span className="kpi-icon amber">
              <ClipboardList size={15} />
            </span>
          </div>
          <div className="kpi-value">{metrics.pendientes}</div>
          <div className="kpi-foot spaced">
            {metrics.revisadas === 0
              ? 'Ninguna revisada todavía'
              : `${metrics.revisadas} revisadas de ${metrics.total}`}
          </div>
        </div>
      </section>

      {/* Buscador + filtros */}
      <section className="nm-filters">
        <div className="filters-top">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Buscar por descripción, entidad o código de contratación"
              value={busquedaGlobal}
              onChange={(e) => setBusquedaGlobal(e.target.value)}
            />
            {busquedaGlobal && (
              <button type="button" className="search-clear" onClick={() => setBusquedaGlobal('')}>
                <X size={15} />
              </button>
            )}
          </div>
          <div className="filters-tabs">
            {[
              { k: 'todos', label: 'Todas', count: metrics.total },
              { k: 'pendientes', label: 'Pendientes', count: metrics.pendientes },
              { k: 'revisados', label: 'Revisadas', count: metrics.revisadas }
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                className={`filters-tab ${filtroEstadoRevision === t.k ? 'is-active' : ''}`}
                onClick={() => setFiltroEstadoRevision(t.k)}
              >
                {t.label} <span className="tab-count">{t.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="filters-grid">
          {/* Departamento (multi) */}
          <div className="filter-field" ref={dropdownRef}>
            <label>Departamento</label>
            <button
              type="button"
              className={`filter-select ${departamentosSeleccionados.length > 0 || todosDepartamentos ? 'is-set' : ''}`}
              onClick={() => setDropdownOpen((v) => !v)}
            >
              <span>{getDepartamentoLabel()}</span>
              <ChevronDown size={17} />
            </button>
            {dropdownOpen && (
              <div className="dept-menu">
                <div className="dept-search">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder="Buscar departamento…"
                    value={searchDepartamento}
                    onChange={(e) => setSearchDepartamento(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="dept-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setTodosDepartamentos(true);
                      setDepartamentosSeleccionados([]);
                    }}
                  >
                    Seleccionar todos
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTodosDepartamentos(false);
                      setDepartamentosSeleccionados([]);
                    }}
                  >
                    Limpiar
                  </button>
                </div>
                <div className="dept-list">
                  {departamentosFiltrados.length > 0 ? (
                    departamentosFiltrados.map((dep) => {
                      const checked = departamentosSeleccionados.includes(dep) || todosDepartamentos;
                      return (
                        <button
                          key={dep}
                          type="button"
                          className={`dept-item ${checked ? 'is-checked' : ''}`}
                          onClick={() => toggleDepartamento(dep)}
                        >
                          <span className="dept-box">{checked && <Check size={12} strokeWidth={3} />}</span>
                          <span className="dept-name">{dep}</span>
                        </button>
                      );
                    })
                  ) : (
                    <div className="dept-empty">Sin resultados</div>
                  )}
                </div>
                <div className="dept-footer">
                  <button type="button" onClick={() => setDropdownOpen(false)}>
                    Aplicar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tipo */}
          <div className="filter-field">
            <label>Tipo de contrato</label>
            <div className={`filter-select-native ${filtroTipo ? 'is-set' : ''}`}>
              <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="">Todos los tipos</option>
                {tiposUnicos.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {getTipoInfo(tipo).label}
                  </option>
                ))}
              </select>
              <ChevronDown size={17} />
            </div>
          </div>

          {/* Cierre */}
          <div className="filter-field">
            <label>Cierre de cotización</label>
            <div className={`filter-select-native ${filtroCierre !== 'todos' ? 'is-set' : ''}`}>
              <select value={filtroCierre} onChange={(e) => setFiltroCierre(e.target.value)}>
                {Object.entries(CIERRE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <ChevronDown size={17} />
            </div>
          </div>

          {/* Orden */}
          <div className="filter-field">
            <label>Ordenar por</label>
            <div className="filter-select-native has-value">
              <select value={ordenarPor} onChange={(e) => setOrdenarPor(e.target.value)}>
                {Object.entries(ORDEN_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <ChevronDown size={17} />
            </div>
          </div>

          {/* Filtros activos */}
          <div className="filters-actions">
            {activeChips.map((chip) => (
              <button key={chip.key} type="button" className="chip" onClick={chip.onRemove} title={chip.label}>
                <span className="chip-text">{chip.label}</span>
                <X size={14} />
              </button>
            ))}
            {activeChips.length > 0 && (
              <button type="button" className="chips-clear" onClick={clearFilters}>
                Limpiar
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Estados de carga / vacío */}
      {loading && data.length === 0 && (
        <div className="nm-state">
          <Spinner className="nm-spinner" />
          <p>Cargando convocatorias vigentes…</p>
          <div
            className="mv-progress"
            role="progressbar"
            aria-valuenow={progreso}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${progreso}%` }} />
          </div>
        </div>
      )}

      {!loading && data.length === 0 && !error && (
        <div className="nm-state">
          <Inbox size={40} strokeWidth={1.5} />
          <h3>No hay convocatorias vigentes</h3>
          <p>Aquí aparecen los contratos menores que siguen abiertos a cotización.</p>
        </div>
      )}

      {/* Resultados */}
      {data.length > 0 && (
        <section className="nm-results">
          <div className="results-head">
            <span className="results-count">
              <b>
                {dataFiltrada.length} {dataFiltrada.length === 1 ? 'convocatoria' : 'convocatorias'}
              </b>{' '}
              con estos filtros
            </span>
            <div className="results-legend">
              <span className="lg-hoy">
                <i />
                Cierra hoy
              </span>
              <span className="lg-manana">
                <i />
                Cierra mañana
              </span>
              <span className="lg-holgura">
                <i />
                Con holgura
              </span>
            </div>
          </div>

          {dataFiltrada.length === 0 ? (
            <div className="nm-state inner">
              <Search size={34} strokeWidth={1.5} />
              <h3>Sin resultados</h3>
              <p>Ajusta los filtros para encontrar más convocatorias.</p>
              <button type="button" className="nm-btn ghost" onClick={clearFilters}>
                Limpiar filtros
              </button>
            </div>
          ) : isMobile ? (
            // ---------- MÓVIL: tarjetas ----------
            <div className="nm-cards">
              {dataPaginada.map((proceso, index) => {
                const urg = getUrgencia(proceso);
                const tipo = getTipoInfo(proceso.TIPO);
                const reviewed = isProcessReviewed(proceso);
                return (
                  <article
                    key={`${proceso['COD CONTRATACIÓN']}-${index}`}
                    className={`nm-card lvl-${urg.level} ${reviewed ? 'is-reviewed' : ''}`}
                  >
                    <div className="card-top">
                      <RowCheckbox
                        checked={reviewed}
                        disabled={!user?.email}
                        onToggle={() => toggleProcessReviewed(proceso, user)}
                      />
                      <CopyNomenclatura code={proceso['COD CONTRATACIÓN']} />
                      <span className={`badge-tipo ${tipo.cls}`}>
                        <span className="dot" />
                        {tipo.label}
                      </span>
                    </div>
                    {(reviewed || urg.label) && (
                      <div className="card-urg">
                        {reviewed && !urg.closed ? (
                          <span className="badge-urg reviewed">
                            <Check size={12} strokeWidth={3} />
                            Revisada
                          </span>
                        ) : (
                          <span className={`badge-urg ${urg.level}`}>{urg.label}</span>
                        )}
                      </div>
                    )}
                    <h3 className="conv-title" onClick={() => openModal(proceso)}>
                      {proceso['DESCRIPCIÓN'] || 'Sin descripción'}
                    </h3>
                    <div className="conv-entity">{proceso.ENTIDAD || '-'}</div>
                    <div className="card-meta">
                      <div>
                        <span className="meta-k">Ubicación</span>
                        <span className="meta-v">
                          {[proceso.DPTO, proceso.PROVINCIA, proceso.DISTRITO].filter(Boolean).join(' · ') || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="meta-k">Cotización</span>
                        <span className="meta-v">{renderCotizacion(proceso)}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button type="button" className="act-detail" onClick={() => openModal(proceso)}>
                        Ver detalle
                      </button>
                      <button
                        type="button"
                        className="act-bases"
                        onClick={() => openPDF(proceso['URL_TDR'])}
                        disabled={!proceso['URL_TDR'] || proceso['URL_TDR'].trim() === ''}
                      >
                        Requerimiento
                      </button>
                      <CalendarButton process={proceso} user={user} enCalendario={codigosEnCalendario} onAdded={registrarEnCalendario} />
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            // ---------- ESCRITORIO: tabla grid ----------
            <div className="nm-table">
              <div className="nm-thead">
                <div className="c-rev">REV</div>
                <div className="c-conv">CONVOCATORIA</div>
                <div className="c-tipo">TIPO</div>
                <div className="c-ubi">UBICACIÓN</div>
                <div className="c-cot">COTIZACIÓN</div>
                <div className="c-acc">ACCIONES</div>
              </div>
              {dataPaginada.map((proceso, index) => {
                const urg = getUrgencia(proceso);
                const tipo = getTipoInfo(proceso.TIPO);
                const reviewed = isProcessReviewed(proceso);
                return (
                  <div
                    key={`${proceso['COD CONTRATACIÓN']}-${index}`}
                    className={`nm-row lvl-${urg.level} ${reviewed ? 'is-reviewed' : ''}`}
                  >
                    <div className="c-rev">
                      <RowCheckbox
                        checked={reviewed}
                        disabled={!user?.email}
                        onToggle={() => toggleProcessReviewed(proceso, user)}
                      />
                    </div>
                    <div className="c-conv">
                      <div className="conv-top">
                        <CopyNomenclatura code={proceso['COD CONTRATACIÓN']} />
                        {reviewed && !urg.closed && (
                          <span className="badge-urg reviewed">
                            <Check size={12} strokeWidth={3} />
                            Revisada
                          </span>
                        )}
                      </div>
                      <div className="conv-title" onClick={() => openModal(proceso)}>
                        {proceso['DESCRIPCIÓN'] || 'Sin descripción'}
                      </div>
                      <div className="conv-entity">{proceso.ENTIDAD || '-'}</div>
                    </div>
                    <div className="c-tipo">
                      <span className={`badge-tipo ${tipo.cls}`}>
                        <span className="dot" />
                        {tipo.label}
                      </span>
                    </div>
                    <div className="c-ubi">
                      <span className="ubi-dpto">{proceso.DPTO || '-'}</span>
                      {(proceso.PROVINCIA || proceso.DISTRITO) && (
                        <span className="ubi-sub">
                          {[proceso.PROVINCIA, proceso.DISTRITO].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    <div className="c-cot">
                      {urg.label && (
                        <div className="cot-status">
                          <span className={`badge-urg ${urg.level}`}>
                            {!urg.closed && urg.dayDiff === 0 ? 'Cierra hoy' : urg.label}
                          </span>
                        </div>
                      )}
                      {renderCotizacion(proceso)}
                    </div>
                    <div className="c-acc">
                      <button
                        type="button"
                        className={`act-detail ${reviewed ? 'outline' : ''}`}
                        onClick={() => openModal(proceso)}
                      >
                        Ver detalle
                      </button>
                      <button
                        type="button"
                        className="nm-icon-btn"
                        title="Requerimiento"
                        onClick={() => openPDF(proceso['URL_TDR'])}
                        disabled={!proceso['URL_TDR'] || proceso['URL_TDR'].trim() === ''}
                      >
                        <FaFilePdf size={17} aria-hidden="true" />
                      </button>
                      <CalendarButton process={proceso} user={user} variant="icon" enCalendario={codigosEnCalendario} onAdded={registrarEnCalendario} />
                      <button
                        type="button"
                        className="nm-icon-btn nm-whatsapp"
                        title="Compartir por WhatsApp"
                        aria-label="Compartir convocatoria por WhatsApp"
                        onClick={() => compartirWhatsApp(proceso)}
                      >
                        <FaWhatsapp aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {renderPagination(false)}
            </div>
          )}

          {isMobile && dataFiltrada.length > 0 && renderPagination(true)}
        </section>
      )}

      {/* Modal de detalle */}
      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
        size="lg"
        contentClassName="nm-modal-content"
        className="nm-modal"
      >
        {selectedContract &&
          (() => {
            const urg = getUrgencia(selectedContract);
            const tipo = getTipoInfo(selectedContract.TIPO);
            const reviewed = isProcessReviewed(selectedContract);
            const pub = parseDateTime(selectedContract._fecha_publicacion_raw);
            const ini = parseDateTime(selectedContract._inicio_cotizacion_raw);
            const fin = parseDateTime(selectedContract._fin_cotizacion_raw);
            const hasTDR = selectedContract['URL_TDR'] && selectedContract['URL_TDR'].trim() !== '';
            const cod = selectedContract['COD CONTRATACIÓN'] || '—';
            const ubicacion = [selectedContract.DPTO, selectedContract.PROVINCIA, selectedContract.DISTRITO]
              .filter(Boolean).join(' · ') || '-';

            // Colores del badge de urgencia
            const urgTone = {
              urgent: { color: '#b3261e', bg: '#fdeceb' },
              soon: { color: '#8a5308', bg: '#fdf3e6' },
              normal: { color: '#5b6779', bg: '#eef1f5' },
              closed: { color: '#5b6779', bg: '#eef1f5' },
              none: { color: '#5b6779', bg: '#eef1f5' }
            }[urg.level] || { color: '#5b6779', bg: '#eef1f5' };
            const cierreCorto = urg.closed ? 'cerrada'
              : urg.dayDiff === 0 ? 'hoy'
              : urg.dayDiff === 1 ? 'mañana'
              : urg.dayDiff != null ? `en ${urg.dayDiff} días` : 'sin fecha';

            const fechaHora = (d) => (d ? `${fmtFecha(d)}${fmtHora(d) ? ` · ${fmtHora(d)}` : ''}` : '—');
            const enCurso = ini && fin && ini.getTime() <= Date.now() && Date.now() <= fin.getTime();
            const estadoFecha = (d, activo) => {
              if (activo) return { label: 'en curso', color: '#0f766e', bg: '#d6f0ea' };
              if (!d) return null;
              const now = new Date();
              const dd = Math.round(
                (new Date(d.getFullYear(), d.getMonth(), d.getDate()) -
                  new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000
              );
              if (dd < 0) return { label: 'vencido', color: '#5b6779', bg: '#eef1f5' };
              if (dd === 0) return { label: 'hoy', color: '#b3261e', bg: '#fdeceb' };
              if (dd === 1) return { label: 'mañana', color: '#8a5308', bg: '#fdf3e6' };
              return { label: `en ${dd} días`, color: dd <= 6 ? '#8a5308' : '#5b6779', bg: dd <= 6 ? '#fdf3e6' : '#eef1f5' };
            };
            const cronoRow = (label, d, color, activo) => {
              const est = estadoFecha(d, activo);
              return (
                <div className="crono-row">
                  <div className="crono-rail"><span className="crono-dot" style={{ background: color }} /></div>
                  <div className="crono-main">
                    <div className="crono-label">{label}</div>
                    <div className="crono-date">{fechaHora(d)}</div>
                  </div>
                  {est && <span className="crono-badge" style={{ color: est.color, background: est.bg }}>{est.label}</span>}
                </div>
              );
            };

            return (
              <>
                <div className="modal-head">
                  <div className="modal-head-text">
                    <div className="modal-badges">
                      <span className={`badge-tipo ${tipo.cls}`}>{tipo.label}</span>
                      <span className="badge-cierre" style={{ color: urgTone.color, background: urgTone.bg }}>
                        <b>{urg.closed ? 'Culminada' : `Cierra ${cierreCorto}`}</b>
                        {fin && fmtHora(fin) && <small>{fmtHora(fin)}</small>}
                      </span>
                      <span className={`modal-status ${reviewed ? 'is-on' : ''}`}>
                        {reviewed ? <><Check size={13} strokeWidth={3} /> Revisada</> : 'Pendiente de revisión'}
                      </span>
                    </div>
                    <h2 className="modal-title">{selectedContract['DESCRIPCIÓN'] || 'Sin descripción'}</h2>
                    <div className="modal-code">
                      <span className="modal-code-txt">{cod}</span>
                      <button
                        type="button"
                        className="modal-code-copy"
                        onClick={() => {
                          navigator.clipboard.writeText(cod);
                          setCopiedCode(true);
                          setTimeout(() => setCopiedCode(false), 1500);
                        }}
                      >
                        {copiedCode ? <><Check size={13} /> Copiado</> : <><Copy size={13} /> Copiar código</>}
                      </button>
                    </div>
                  </div>
                  <button type="button" className="modal-close" onClick={closeModal}>
                    <X size={22} />
                  </button>
                </div>

                <div className="modal-body">
                  <div className="modal-cols">
                    <div className="modal-main">
                      <div className="modal-section-label">DATOS DE LA CONVOCATORIA</div>
                      <div className="modal-grid">
                        <div className="span2"><label>ENTIDAD</label><span>{selectedContract.ENTIDAD || '—'}</span></div>
                        <div><label>UBICACIÓN</label><span>{ubicacion}</span></div>
                        <div><label>TIPO</label><span>{tipo.label}</span></div>
                      </div>

                      <div className="modal-section-label">DOCUMENTOS</div>
                      {hasTDR ? (
                        <button
                          type="button"
                          className="modal-doc"
                          onClick={() => openPDF(selectedContract['URL_TDR'])}
                        >
                          <span className="doc-ic"><FaFilePdf size={19} aria-hidden="true" /></span>
                          <div className="doc-text">
                            <div className="doc-title">Requerimiento</div>
                            <div className="doc-sub">PDF · se abre en una pestaña nueva</div>
                          </div>
                          <ExternalLink size={16} className="doc-ext" />
                        </button>
                      ) : (
                        <div className="modal-doc empty">Sin documento disponible</div>
                      )}
                    </div>

                    <div className="modal-side">
                      <div className="crono-head">
                        <span className="modal-section-label">CRONOGRAMA</span>
                        {urg.label && (
                          <span className={`crono-count ${urg.level === 'urgent' || urg.level === 'soon' ? 'is-urgent' : ''}`}>
                            <Clock size={12} /> {urg.label}
                          </span>
                        )}
                      </div>
                      <div className="cronograma">
                        {cronoRow('Publicación', pub, '#cbd5e1')}
                        {cronoRow('Inicio de cotización', ini, '#0f766e', enCurso)}
                        {cronoRow('Fin de cotización', fin, '#b3261e')}
                      </div>

                      <div className="modal-section-label">AGREGAR AL SEGUIMIENTO</div>
                      <div className="modal-seg">
                        <CalendarButton process={selectedContract} user={user} variant="seg" enCalendario={codigosEnCalendario} onAdded={registrarEnCalendario} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-foot">
                  <button
                    type="button"
                    className="modal-review"
                    onClick={() => toggleProcessReviewed(selectedContract, user)}
                    disabled={!user?.email}
                  >
                    <RowCheckbox
                      checked={reviewed}
                      disabled={!user?.email}
                      onToggle={() => toggleProcessReviewed(selectedContract, user)}
                    />
                    <span>{reviewed ? 'Marcada como revisada' : 'Marcar como revisada'}</span>
                  </button>
                  <div className="modal-foot-actions">
                    <button
                      type="button"
                      className="modal-primary modal-whatsapp"
                      onClick={() => compartirWhatsApp(selectedContract)}
                    >
                      <FaWhatsapp size={17} aria-hidden="true" />
                      <span>Compartir por WhatsApp</span>
                    </button>
                    <button
                      type="button"
                      className="modal-primary"
                      disabled={!hasTDR}
                      onClick={() => openPDF(selectedContract['URL_TDR'])}
                    >
                      <ExternalLink size={16} />
                      <span>Abrir requerimiento</span>
                    </button>
                  </div>
                </div>
              </>
            );
          })()}
      </Modal>
    </div>
  );
};

export default MenoresVigentes;
